import React, { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "reactstrap";
import { useAssignNumber } from "src/numbers/hooks/useAssignNumber";
import type { ResponseError } from "src/shared/actions/objects/ResponseError";
import { ReactQueryResult } from "src/shared/components/ReactQueryResult";

export interface ShortCodeVerifyProps {
  countryCode: string;
  providerId: string | number | undefined;
  mobileVirtualNumber: string | undefined;
  onNumberVerifySuccess: (no: string) => void;
  disabled?: boolean;
}

export const ShortCodeVerify = ({
  countryCode,
  providerId,
  mobileVirtualNumber,
  onNumberVerifySuccess,
  disabled,
}: ShortCodeVerifyProps) => {
  const mutationResult = useAssignNumber(countryCode);
  const { mutateAsync } = mutationResult;

  const handleAssignClick = useCallback(async () => {
    try {
      const { mobileVirtualNumber: returnedNumber } = await mutateAsync({
        providerId: Number(providerId),
        mobileVirtualNumber,
        mvnType: "CSC",
      });
      onNumberVerifySuccess(returnedNumber);
    } catch (ex) {
      // handled in <ReactQueryResult />
    }
  }, [mobileVirtualNumber, mutateAsync, onNumberVerifySuccess, providerId]);

  const assignNumberError = useMemo(() => {
    if (mutationResult.isError) {
      const response = (mutationResult.error as ResponseError)?.response;
      if (response?.status === 404) {
        return <FormattedMessage id="numbers.form.shortCode.unavailable" />;
      }
    }

    return null;
  }, [mutationResult.error, mutationResult.isError]);

  return (
    <>
      <p>
        <Button
          type="button"
          color="primary"
          onClick={handleAssignClick}
          disabled={disabled}
        >
          <FormattedMessage id="numbers.form.shortCode.verifyNumber" />
        </Button>
      </p>
      <ReactQueryResult {...mutationResult} error={assignNumberError} />
    </>
  );
};
